import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import PhoneInputLib, { parsePhoneNumber, getCountryCallingCode } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import ptLabels from 'react-phone-number-input/locale/pt.json';
import enLabels from 'react-phone-number-input/locale/en.json';
import { getDefaultCountryISO } from '../../utils/phone';
import CountrySelect from './CountrySelect';

/**
 * PhoneInput - Wrapper around react-phone-number-input.
 *
 * Provides real-time formatting as the user types, with automatic
 * country detection from the value and all countries available.
 *
 * VALUE CONTRACT:
 *   value:    E.164 with "+" prefix, e.g. "+5511987654321" (or undefined/empty)
 *   onChange: (e164Value: string | undefined) => void
 *
 * CONVERSION (use utils/phone.js):
 *   toE164(dbValue)    -> populate from DB
 *   fromE164(e164)     -> send to API/DB
 */
const PhoneInput = ({
  value,
  onChange,
  defaultCountry,
  placeholder,
  disabled,
  required,
  id,
  name,
  className,
  style,
}) => {
  const { i18n } = useTranslation();
  const resolvedDefaultCountry = useMemo(
    () => defaultCountry || getDefaultCountryISO(),
    [defaultCountry]
  );
  // Nomes de país traduzidos (a busca do CountrySelect casa com o nome localizado)
  const labels = (i18n.language || '').toLowerCase().startsWith('pt') ? ptLabels : enLabels;

  // Colar (paste) robusto — principalmente no mobile. Intercepta o evento de paste,
  // monta o E.164 a partir do texto colado e atualiza via onChange. Isso contorna o
  // tratamento de caret do react-phone-number-input, que costuma falhar ao colar no celular.
  const handlePasteCapture = (event) => {
    try {
      const clip = event.clipboardData || window.clipboardData;
      const raw = clip ? clip.getData('text') : '';
      if (!raw || !raw.trim()) return; // sem acesso ao texto -> mantém o comportamento padrão
      const text = raw.trim();

      let e164 = null;
      try {
        const parsed = text.startsWith('+')
          ? parsePhoneNumber(text)
          : parsePhoneNumber(text, resolvedDefaultCountry);
        if (parsed && parsed.number) e164 = parsed.number;
      } catch (_) { /* parser falhou -> usa o fallback abaixo */ }

      if (!e164) {
        const digits = text.replace(/[^\d]/g, '');
        if (!digits) return;
        e164 = text.includes('+')
          ? `+${digits}`
          : `+${getCountryCallingCode(resolvedDefaultCountry)}${digits}`;
      }

      event.preventDefault();
      if (onChange) onChange(e164);
    } catch (_) {
      // qualquer erro -> deixa o paste padrão acontecer
    }
  };

  return (
    <PhoneInputWrapper style={style} className={className} onPasteCapture={handlePasteCapture}>
      <PhoneInputLib
        international
        countryCallingCodeEditable={false}
        smartCaret={false}
        defaultCountry={resolvedDefaultCountry}
        countrySelectComponent={CountrySelect}
        labels={labels}
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder || ''}
        disabled={disabled}
        id={id}
        name={name || id}
        required={required}
      />
    </PhoneInputWrapper>
  );
};

const PhoneInputWrapper = styled.div`
  /* Reset external styles to prevent double-border effect */
  border: none !important;
  padding: 0 !important;
  background: transparent !important;
  box-shadow: none !important;

  /* Container styling to match existing design */
  .PhoneInput {
    display: flex;
    align-items: center;
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    background: #fff;
    transition: border-color 0.2s, box-shadow 0.2s;
    overflow: hidden;

    &:focus-within {
      border-color: #1095F3;
      box-shadow: 0 0 0 2px rgba(16, 149, 243, 0.15);
    }
  }

  /* Country selector area */
  .PhoneInputCountry {
    display: flex;
    align-items: center;
    padding: 0 8px 0 12px;
    gap: 6px;
    background: #f8f9fa;
    height: 100%;
    min-height: 42px;
    border-right: 1px solid #d9d9d9;
    flex-shrink: 0;
    cursor: pointer;
  }

  /* Country flag icon */
  .PhoneInputCountryIcon {
    width: 24px;
    height: 18px;
    border-radius: 2px;
    overflow: hidden;
    flex-shrink: 0;
  }

  .PhoneInputCountryIcon--border {
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
  }

  .PhoneInputCountryIconImg {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  /* Country select dropdown (invisible overlay) */
  .PhoneInputCountrySelect {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
    font-size: 14px;
    z-index: 1;
  }

  /* Country select arrow */
  .PhoneInputCountrySelectArrow {
    display: block;
    width: 6px;
    height: 6px;
    border-style: solid;
    border-color: #666;
    border-top-width: 0;
    border-left-width: 0;
    border-right-width: 1.5px;
    border-bottom-width: 1.5px;
    transform: rotate(45deg);
    margin-left: 4px;
    flex-shrink: 0;
  }

  /* Country calling code text (+55, +90, etc) */
  .PhoneInputCountryIconImg + .PhoneInputCountrySelectArrow {
    margin-left: 6px;
  }

  /* The actual phone number input */
  .PhoneInputInput {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    padding: 10px 12px;
    font-size: 15px;
    background: transparent;
    color: #333;
    font-family: inherit;

    &::placeholder {
      color: #bbb;
    }

    &:disabled {
      background: #f5f5f5;
      color: #999;
      cursor: not-allowed;
    }
  }

  /* Remove default library border */
  .PhoneInput--focus {
    /* Handled by :focus-within above */
  }
`;

export default PhoneInput;
